import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { useLocationListing } from '../hooks/useLocationListing';
import { LocationControls, DistanceBadge } from '../components/LocationControls';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import GuestBanner from '../components/GuestBanner';
import GuestAccessibleBadge from '../components/GuestAccessibleBadge';
import CommunitySubmitBanner from '../components/CommunitySubmitBanner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Search,
  MapPin,
  Star,
  Phone,
  Globe,
  Clock,
  AlertCircle,
  Filter,
  Plus,
  ThumbsUp,
  Award,
  CheckCircle,
  Loader2,
  Stethoscope,
  Scissors,
  GraduationCap,
  Home,
  Dog,
  Building,
  Camera,
  Shield,
  ExternalLink,
  Navigation,
  Route,
  UserPlus,
  Heart
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  veterinarian: Stethoscope,
  groomer: Scissors,
  trainer: GraduationCap,
  pet_sitter: Home,
  dog_walker: Dog,
  boarding: Building,
  daycare: Building,
  breeder: Dog,
  rescue: Dog,
  pet_store: Building,
  pet_photography: Camera,
  pet_insurance: Shield,
  other: Building
};

const Services = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Use the location listing hook
  const {
    items: services,
    loading,
    userLocation,
    locationLoading,
    requestLocation,
    clearLocation,
    sortBy,
    setSortBy,
    maxDistance,
    setMaxDistance,
    filters,
    updateFilter,
    refetch
  } = useLocationListing({
    endpoint: '/api/services',
    buildParams: useCallback((filters) => {
      const params = {};
      if (filters.category) params.category = filters.category;
      if (filters.city) params.city = filters.city;
      if (filters.min_rating) params.min_rating = parseFloat(filters.min_rating);
      if (filters.search) params.search = filters.search;
      return params;
    }, []),
    initialFilters: {
      category: searchParams.get('category') || '',
      city: searchParams.get('city') || '',
      min_rating: searchParams.get('min_rating') || '',
      search: searchParams.get('search') || ''
    }
  });
  
  const [categories, setCategories] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [myPets, setMyPets] = useState([]);
  const [addToCareTeamOpen, setAddToCareTeamOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedPetsForCareTeam, setSelectedPetsForCareTeam] = useState([]);
  const [addingToCareTeam, setAddingToCareTeam] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // New service form
  const [newService, setNewService] = useState({
    name: '',
    category: 'veterinarian',
    description: '',
    services_offered: [],
    price_range: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    email: '',
    website: '',
    hours_of_operation: '',
    accepts_emergency: false,
    is_mobile: false,
    specialties: [],
    image_url: ''
  });

  useEffect(() => {
    fetchCategories();
    if (user && token) {
      fetchMyPets();
    }
  }, [user, token]);

  const fetchMyPets = async () => {
    try {
      const response = await axios.get(`${API}/pets/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyPets(response.data);
    } catch (error) {
      console.error('Error fetching pets:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/services/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    refetch();
  };

  const handleCreateService = async () => {
    if (!newService.name || !newService.description) {
      toast.error('Please fill in name and description');
      return;
    }
    
    setCreating(true);
    try {
      const serviceData = {
        ...newService,
        services_offered: newService.services_offered.length > 0 
          ? newService.services_offered 
          : newService.description.split(',').map(s => s.trim()).filter(Boolean).slice(0, 5),
        specialties: newService.specialties.length > 0
          ? newService.specialties
          : []
      };
      
      await axios.post(`${API}/services`, serviceData);
      toast.success('Service listing created!');
      setAddDialogOpen(false);
      setNewService({
        name: '',
        category: 'veterinarian',
        description: '',
        services_offered: [],
        price_range: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        phone: '',
        email: '',
        website: '',
        hours_of_operation: '',
        accepts_emergency: false,
        is_mobile: false,
        specialties: [],
        image_url: ''
      });
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create service');
    } finally {
      setCreating(false);
    }
  };

  const handleAddToCareTeam = async () => {
    if (!selectedService) return;

    setAddingToCareTeam(true);
    try {
      // Map service category to care provider type
      const providerTypeMap = {
        veterinarian: 'veterinarian',
        groomer: 'groomer',
        trainer: 'trainer',
        dog_walker: 'dog_walker',
        pet_sitter: 'pet_sitter',
        boarding: 'boarding',
        daycare: 'daycare',
        emergency_vet: 'emergency_vet',
        pet_store: 'pet_supply',
        pet_insurance: 'insurance',
        pet_photography: 'other',
        rescue: 'other',
        other: 'other'
      };

      await axios.post(`${API}/care-team/providers`, {
        name: selectedService.name,
        provider_type: providerTypeMap[selectedService.category] || 'other',
        phone: selectedService.phone || '',
        email: selectedService.email || '',
        address: selectedService.address || `${selectedService.city || ''}, ${selectedService.state || ''}`.trim(),
        website: selectedService.website || '',
        notes: `Added from Services Directory. ${selectedService.description || ''}`.substring(0, 500),
        pet_ids: selectedPetsForCareTeam,
        is_primary: false
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${selectedService.name} added to your Care Team!`);
      setAddToCareTeamOpen(false);
      setSelectedService(null);
      setSelectedPetsForCareTeam([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to care team');
    } finally {
      setAddingToCareTeam(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating
                ? 'fill-amber-400 text-amber-400'
                : 'text-muted-foreground/30'
            }`}
          />
        ))}
      </div>
    );
  };

  const CategoryIcon = ({ category }) => {
    const Icon = categoryIcons[category] || Building;
    return <Icon className="w-5 h-5" />;
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Pet Services"
        description="Discover top-rated veterinarians, groomers, trainers, and more. Read reviews from real pet owners in your community."
        url="/services"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <h1 className="text-4xl font-heading font-medium">
              Find Trusted Pet Services
            </h1>
            <GuestAccessibleBadge className="bg-white/20 text-white" />
          </div>
          <p className="text-primary-foreground/80 text-lg mb-8 max-w-2xl">
            Discover top-rated veterinarians, groomers, trainers, and more. 
            Read reviews from real pet owners in your community.
          </p>
          
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="flex gap-3 max-w-3xl">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Search services, specialties..."
                value={filters.search}
                onChange={(e) => updateFilter('search', e.target.value)}
                className="pl-12 h-12 bg-white text-foreground"
                data-testid="service-search"
              />
            </div>
            <div className="relative">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="City"
                value={filters.city}
                onChange={(e) => updateFilter('city', e.target.value)}
                className="pl-12 h-12 w-48 bg-white text-foreground"
              />
            </div>
            <Button type="submit" className="h-12 px-8 bg-secondary hover:bg-secondary/90">
              Search
            </Button>
          </form>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Guest Banner */}
        <GuestBanner message="Sign up to add reviews, save favorites, and contact service providers" />
        
        {/* Community Submit Banner */}
        <div className="mb-8">
          <CommunitySubmitBanner variant="compact" serviceType="service" />
        </div>
        
        {/* Categories */}
        <div className="mb-8">
          <h2 className="text-xl font-heading font-medium mb-4">Browse by Category</h2>
          <div className="flex flex-wrap gap-3">
            <Button
              variant={filters.category === '' ? 'default' : 'outline'}
              onClick={() => updateFilter('category', '')}
              className={filters.category === '' ? 'btn-primary' : ''}
            >
              All Services
            </Button>
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={filters.category === cat.id ? 'default' : 'outline'}
                onClick={() => updateFilter('category', cat.id)}
                className={`${filters.category === cat.id ? 'btn-primary' : ''} flex items-center gap-2`}
              >
                <CategoryIcon category={cat.id} />
                {cat.name}
                <Badge variant="secondary" className="ml-1">{cat.count}</Badge>
              </Button>
            ))}
          </div>
        </div>

        {/* Header with Actions */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-4">
            <p className="text-muted-foreground">
              {loading ? 'Loading...' : `${services.length} services found`}
            </p>
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <LocationControls
              userLocation={userLocation}
              locationLoading={locationLoading}
              onRequestLocation={requestLocation}
              onClearLocation={clearLocation}
              maxDistance={maxDistance}
              onMaxDistanceChange={setMaxDistance}
              sortBy={sortBy}
              onSortByChange={setSortBy}
            />
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Filters
            </Button>
            {user && (
              <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-service-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your Business
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Add Your Service</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        placeholder="Business Name *"
                        value={newService.name}
                        onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                      />
                      <Select
                        value={newService.category}
                        onValueChange={(val) => setNewService({ ...newService, category: val })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <Textarea
                      placeholder="Description *"
                      value={newService.description}
                      onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                      rows={3}
                    />
                    
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        placeholder="Phone"
                        value={newService.phone}
                        onChange={(e) => setNewService({ ...newService, phone: e.target.value })}
                      />
                      <Input
                        placeholder="Email"
                        value={newService.email}
                        onChange={(e) => setNewService({ ...newService, email: e.target.value })}
                      />
                    </div>
                    
                    <Input
                      placeholder="Website URL"
                      value={newService.website}
                      onChange={(e) => setNewService({ ...newService, website: e.target.value })}
                    />
                    
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        placeholder="Street Address"
                        value={newService.address}
                        onChange={(e) => setNewService({ ...newService, address: e.target.value })}
                      />
                      <Input
                        placeholder="City"
                        value={newService.city}
                        onChange={(e) => setNewService({ ...newService, city: e.target.value })}
                      />
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <Input
                        placeholder="State"
                        value={newService.state}
                        onChange={(e) => setNewService({ ...newService, state: e.target.value })}
                      />
                      <Input
                        placeholder="ZIP Code"
                        value={newService.zip_code}
                        onChange={(e) => setNewService({ ...newService, zip_code: e.target.value })}
                      />
                      <Select
                        value={newService.price_range}
                        onValueChange={(val) => setNewService({ ...newService, price_range: val })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Price Range" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="$">$ - Budget</SelectItem>
                          <SelectItem value="$$">$$ - Moderate</SelectItem>
                          <SelectItem value="$$$">$$$ - Premium</SelectItem>
                          <SelectItem value="$$$$">$$$$ - Luxury</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <Input
                      placeholder="Hours (e.g., Mon-Fri 9am-6pm)"
                      value={newService.hours_of_operation}
                      onChange={(e) => setNewService({ ...newService, hours_of_operation: e.target.value })}
                    />
                    
                    <Input
                      placeholder="Image URL"
                      value={newService.image_url}
                      onChange={(e) => setNewService({ ...newService, image_url: e.target.value })}
                    />
                    
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newService.accepts_emergency}
                          onChange={(e) => setNewService({ ...newService, accepts_emergency: e.target.checked })}
                          className="rounded"
                        />
                        <span className="text-sm">Accepts Emergency Cases</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newService.is_mobile}
                          onChange={(e) => setNewService({ ...newService, is_mobile: e.target.checked })}
                          className="rounded"
                        />
                        <span className="text-sm">Mobile Service (We Come to You)</span>
                      </label>
                    </div>
                    
                    <Button 
                      className="w-full btn-primary" 
                      onClick={handleCreateService}
                      disabled={creating}
                    >
                      {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      Create Listing
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <Select value={filters.min_rating || "any"} onValueChange={(val) => updateFilter('min_rating', val === "any" ? "" : val)}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Min Rating" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any Rating</SelectItem>
                    <SelectItem value="4">4+ Stars</SelectItem>
                    <SelectItem value="3">3+ Stars</SelectItem>
                    <SelectItem value="2">2+ Stars</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => {
                  updateFilter('min_rating', '');
                  updateFilter('city', '');
                  updateFilter('search', '');
                  updateFilter('category', '');
                }}>
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Services List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : services.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Search className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="font-heading text-xl mb-2">No services found</h3>
              <p className="text-muted-foreground mb-4">
                Try adjusting your search or filters
              </p>
              {user && (
                <Button onClick={() => setAddDialogOpen(true)} className="btn-primary">
                  Be the first to add a service
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {services.map((service) => (
              <Card 
                key={service.id} 
                className="card-interactive cursor-pointer overflow-hidden"
                onClick={() => navigate(`/services/${service.id}`)}
                data-testid={`service-card-${service.id}`}
              >
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row">
                    {/* Image */}
                    <div className="w-full md:w-48 h-48 md:h-auto bg-muted flex-shrink-0">
                      {service.image_url ? (
                        <img 
                          src={service.image_url} 
                          alt={service.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <CategoryIcon category={service.category} />
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 p-6">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-heading text-xl font-medium">{service.name}</h3>
                            {service.license_verified && (
                              <CheckCircle className="w-5 h-5 text-primary" title="Verified" />
                            )}
                          </div>
                          <Badge variant="secondary" className="capitalize">
                            {service.category.replace('_', ' ')}
                          </Badge>
                        </div>
                        {service.price_range && (
                          <span className="text-lg font-medium text-primary">{service.price_range}</span>
                        )}
                      </div>
                      
                      {/* Rating */}
                      <div className="flex items-center gap-3 mb-3">
                        {renderStars(service.average_rating)}
                        <span className="font-medium">{service.average_rating.toFixed(1)}</span>
                        <span className="text-muted-foreground">
                          ({service.review_count} reviews)
                        </span>
                        {service.recommendation_count > 0 && (
                          <span className="flex items-center gap-1 text-sm text-primary">
                            <ThumbsUp className="w-4 h-4" />
                            {Math.round((service.recommendation_count / service.review_count) * 100)}% recommend
                          </span>
                        )}
                      </div>
                      
                      <p className="text-muted-foreground line-clamp-2 mb-4">{service.description}</p>
                      
                      {/* Details */}
                      <div className="flex flex-wrap gap-4 text-sm">
                        {service.city && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <MapPin className="w-4 h-4" />
                            {service.city}{service.state && `, ${service.state}`}
                          </span>
                        )}
                        {service.distance_miles !== null && service.distance_miles !== undefined && (
                          <Badge variant="secondary" className="flex items-center gap-1">
                            <Route className="w-3 h-3" />
                            {service.distance_miles} mi away
                          </Badge>
                        )}
                        {service.phone && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Phone className="w-4 h-4" />
                            {service.phone}
                          </span>
                        )}
                        {service.hours_of_operation && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Clock className="w-4 h-4" />
                            {service.hours_of_operation}
                          </span>
                        )}
                      </div>
                      
                      {/* Tags */}
                      <div className="flex flex-wrap gap-2 mt-4">
                        {service.accepts_emergency && (
                          <Badge variant="destructive" className="flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            24/7 Emergency
                          </Badge>
                        )}
                        {service.is_mobile && (
                          <Badge variant="secondary">Mobile Service</Badge>
                        )}
                        {service.specialties?.slice(0, 3).map((spec, idx) => (
                          <Badge key={idx} variant="outline">{spec}</Badge>
                        ))}
                      </div>
                      
                      {/* Add to Care Team Button */}
                      {user && (
                        <div className="mt-4 pt-4 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedService(service);
                              setAddToCareTeamOpen(true);
                            }}
                            data-testid={`add-care-team-${service.id}`}
                          >
                            <Heart className="w-4 h-4" />
                            Add to My Care Team
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add to Care Team Dialog */}
      <Dialog open={addToCareTeamOpen} onOpenChange={setAddToCareTeamOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to My Care Team</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {selectedService && (
              <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <CategoryIcon category={selectedService.category} />
                </div>
                <div>
                  <h3 className="font-medium">{selectedService.name}</h3>
                  <p className="text-sm text-muted-foreground capitalize">
                    {selectedService.category.replace('_', ' ')}
                  </p>
                </div>
              </div>
            )}
            
            {myPets.length > 0 && (
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Assign to pets (optional)
                </label>
                <div className="flex flex-wrap gap-2">
                  {myPets.map(pet => (
                    <Badge
                      key={pet.id}
                      variant={selectedPetsForCareTeam.includes(pet.id) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => {
                        setSelectedPetsForCareTeam(prev =>
                          prev.includes(pet.id)
                            ? prev.filter(id => id !== pet.id)
                            : [...prev, pet.id]
                        );
                      }}
                    >
                      {pet.name}
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Click to select which pets this provider is for
                </p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setAddToCareTeamOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddToCareTeam} disabled={addingToCareTeam}>
              {addingToCareTeam && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Add to Care Team
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Services;
